import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";
import cx from "classnames";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import copy from "copy-to-clipboard";

import { useUserDetails } from "src/utils/collections.helper";

import Reveal from "src/components/animation";
import ContactCardViewToggle from "../../../../../components/contactCardViewToggle";
import Tooltip from "src/components/tooltip";

import { CollectionAsset } from "src/types/collections";
import { CollectionContext } from "src/pages/collection/components/context";

import styles from "./styles.module.scss";
const DefaultThumbnail = '/images/default_user_profile_background.png';

interface ContactCardProps {
  asset: CollectionAsset;
  editable: boolean;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: CollectionAsset) => void;
}

const ContactCard: React.FC<ContactCardProps> = ({
  asset,
  editable,
  onRemove,
}) => {
  const { sectionId: currentSectionIndex } = useParams();

  const collectionContext = useContext(CollectionContext);

  const user = useSelector((state: any) => 
      _.get(state, "users.me", {})
    );
  const { userName, userTitle, userEmail, userImage, userContactNumber, linkedInUrl } = useUserDetails(user);

  const editCollection = useSelector((state: any) =>
    _.get(state, `${collectionContext.editSelector}`),
  );

  const assetViewType = _.get(asset, "metadata.contactCardLayout");
  const [selectedViewType, setSelectedViewType] = useState(assetViewType ?? "split");

  useEffect(() => {
    setSelectedViewType(assetViewType ?? "split");
  }, [assetViewType]);

  const [isHoveredOver, setIsHoveredOver] = useState(false);
  const [showContactNumberCopied, setShowContactNumberCopied] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: asset.id,
    disabled: !editable,
    data: {
      asset: asset,
      type: "asset",
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
  };

  const handleViewSelect = (view: string) => {
    setSelectedViewType(view);
    const contactCard = currentSectionIndex
      ? _.find(
          _.get(
            editCollection,
            `sections[${currentSectionIndex ?? 0}].item.entities`,
            [],
          ),
          (entity) => _.get(entity, "type") === "CONTACT_CARD",
        )
      : _.find(
          editCollection.entities,
          (entity) => _.get(entity, "type") === "CONTACT_CARD",
        );

    collectionContext.updateAssetMetadata(
      currentSectionIndex ? Number(currentSectionIndex) : undefined,
      [
        {
          id: _.get(contactCard, "id") as string,
          metadata: {
            ..._.get(contactCard, "metadata", {}),
            contactCardLayout: view,
          },
        },
      ],
    );
  };

  const handleContactNumberClick = () => {
    copy(userContactNumber);
    setShowContactNumberCopied(true);

    setTimeout(() => setShowContactNumberCopied(false), 5000);
  };

  const redirectLinkedInUrl = () => {  
      if (linkedInUrl) {
        window.open(linkedInUrl, "_blank");
      }
    }

  return (
    <div
      className={cx(styles.card, {
        [styles.editable]: editable,
        [styles.dragging]: isDragging,
        [styles.full]: selectedViewType === "overlay",
      })}
      style={style}
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onMouseEnter={() => setIsHoveredOver(true)}
      onMouseLeave={() => setIsHoveredOver(false)}
    >
      <div className={cx(styles.icon, {})}>
        <div
          className={styles.contactCardImage}
          style={{
            backgroundImage: `url('${userImage || DefaultThumbnail}')`,
          }}
        />
        <div className={styles.vignetteBlur}></div>
      </div>

      <Reveal show={editable}>
        <button className={styles.remove} onClick={handleRemove}>
          <i className="fa-regular fa-circle-xmark"></i>
        </button>
      </Reveal>

      <div className={styles.cardContent}>
        <div className={styles.nameAndTitle}>
          <div className={styles.nameContainer}>
            <Tooltip content={userName} placement="top">
              <p className={cx(styles.name, {
                [styles.fullWidth]: !linkedInUrl,
              })}>
                {userName}
              </p>
            </Tooltip>
            {linkedInUrl && <div
              className={styles.linkedIn}
              onClick={redirectLinkedInUrl}
            >
              <i className={cx(styles.icon, "paperflite-linkedin", styles.linkedInIcon)} />
            </div>}
          </div>
          {userTitle && <p className={styles.title}>
            {userTitle}
          </p>}
        </div>
        <div className={styles.ctaParentContainer}>
          {userEmail && <div className={styles.contentWrapper}>
            <div className={cx(styles.ctaContainer, styles.mail)}>
              <i className={cx(styles.icon, "paperflite-mail")} />
              <p className={styles.content}>
                {userEmail}
              </p>
            </div>
          </div>}
          {userContactNumber && <div
            className={styles.ctaContainer}
            onClick={handleContactNumberClick}
          >
            <i className={cx(styles.icon, "paperflite-phone-call")} />
            <p
              className={cx(styles.content, {
                [styles.showContactNumberCopied]: showContactNumberCopied,
              })}
            >
              {showContactNumberCopied ? "Copied!" : userContactNumber}
            </p>
          </div>}
        </div>
      </div>

      <ContactCardViewToggle
        show={editable && isHoveredOver && !isDragging}
        views={[
          {
            type: "split",
            id: "split",
            icon: "paperflite-split",
          },
          {
            type: "overlay",
            id: "overlay",
            icon: "paperflite-overlay",
          },
        ]}
        selectedView={selectedViewType}
        onViewSelect={handleViewSelect}
      />
    </div>
  );
};

const PlaceholderContactCard: React.FC<any> = () => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)}></div>
    </div>
  );
};

export { ContactCard, type ContactCardProps, PlaceholderContactCard };
